import { TestStatus } from '@shared/types/database'
import * as graph from '@shared/types/graphql'
import * as db from '@shared/types/database'
import { AssetType } from '@alpha/ink'
import A from '@/utilities/alpha'
import store from '@/store'
import { Crumb } from '@/components/Breadcrumbs.vue'
import { ProjectCardData } from '@alpha/ink/src/types/interfaces'

export const isDraftOrSubmitted = (status: TestStatus) => {
  return ['Draft', 'Submitted', 'In Review'].includes(status)
}

export const inProgressOrComplete = (status: TestStatus) => {
  return ['Submitted', 'Complete'].includes(status)
}

export const isCompleted = (status: TestStatus) => {
  return status === 'Complete'
}

export function dbToGraphTestStatus (value: TestStatus): graph.TestStatus {
  switch (value) {
    case TestStatus.Complete:
      return graph.TestStatus.Complete

    case TestStatus.InProgress:
    case TestStatus.InDesign:
      return graph.TestStatus.InProgress

    case TestStatus.InReview:
    case TestStatus.InTesting:
      return graph.TestStatus.InReview

    default:
      return graph.TestStatus.Draft
  }
}

export function livechartsEnabled (test: db.Test, queryOverride: boolean): boolean {
  if (!test) return false
  if (hasVideoAssets(test)) return false
  return !hasAssets(test) || queryOverride
}

function hasAssets (test: db.Test): boolean {
  return test.payload.assets && !!Object.keys(test.payload.assets).length
}

export const hasVideoAssets = (test: db.Test): boolean => {
  return hasAssets(test) && Object.values(test.payload.assets).some(asset => asset.asset_type === AssetType.VIDEO)
}

export const arePrototypesValid = (prototypes: Record<string, db.Prototype>) => {
  let valid = true
  const prototypeKeys = Object.keys(prototypes || {})

  prototypeKeys.forEach((key) => {
    const prototype = prototypes![key]
    if (!prototype.title.trim()) {
      valid = false
    }
    // Must have a URL
    if (!prototype.url?.trim()) {
      valid = false
    }
    if (!A.isValidUrl(prototype.url!)) {
      valid = false
    }
  })

  return valid
}

export function testURL (workspaceId: string, testId: string, projectId?: string | null) {
  if (projectId) {
    return `/workspaces/${workspaceId}/projects/${projectId}/tests/${testId}`
  } else {
    return `/workspaces/${workspaceId}/tests/${testId}`
  }
}

/***
 * With the new test experience, there is a char limit on test titles. If the test title will go over this (legacy tests
 * or cloning), we take first x chars and add a ... to the title.
 * @param title
 */
export const convertTestTitleUsingCharLimit = (title = '') => {
  if (title.length > 60) {
    title = `${title.slice(0, 57)}...`
  }

  return title
}

export const convertAndSaveLegacyTestTitle = async (test: db.Test, testId: string, workspaceId: string) => {
  const title = convertTestTitleUsingCharLimit(test.payload.title)
  let decisionContext = test.payload.decisionContext || ''

  if (title !== test.payload.title) {
    if (!decisionContext.includes('Original title:')) {
      decisionContext = `Original title: ${test.payload.title} ${decisionContext}`
    }

    await store.dispatch.workspace.updateTest({
      testId: testId,
      workspaceId: workspaceId,
      test: { title, decisionContext }
    })
  }
}

export const generateTestCrumbs = (project: ProjectCardData | undefined) : Crumb[] => {
  const workspace = store.state.workspace.workspace
  const result: Crumb[] = []

  result.push({ title: workspace.name, url: `/workspaces/${workspace.id}/projects` })

  if (project) {
    result.push({ title: project.title, url: `/workspaces/${workspace.id}/projects/${project.id}` })
  }

  result.push({ title: 'Test' })

  return result
}
